import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Inbox } from 'lucide-react';
import { fetchInquiries, updateInquiryStatus, type Inquiry } from '../../lib/cms-api';

interface Props {
  notify: (msg: string, type?: 'success' | 'error') => void;
}

const STATUS_LABEL: Record<Inquiry['status'], string> = {
  new: '新查詢',
  read: '已讀',
  replied: '已回覆',
};

const STATUS_STYLE: Record<Inquiry['status'], string> = {
  new: 'bg-amber-100 text-amber-800',
  read: 'bg-blue-100 text-blue-800',
  replied: 'bg-green-100 text-green-800',
};

export default function InquiriesManager({ notify }: Props) {
  const [list, setList] = useState<Inquiry[] | null>(null);
  const [filter, setFilter] = useState<'all' | Inquiry['status']>('all');

  const load = useCallback(async () => {
    try {
      const data = await fetchInquiries();
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      notify('無法載入查詢列表。', 'error');
      setList([]);
    }
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id: string, status: Inquiry['status']) => {
    try {
      await updateInquiryStatus(id, status);
      setList((prev) => (prev ? prev.map((i) => (i.id === id ? { ...i, status } : i)) : prev));
    } catch {
      notify('更新狀態失敗。', 'error');
    }
  };

  const visible = (list || []).filter((i) => filter === 'all' || i.status === filter);

  return (
    <div className="bg-white rounded-lg shadow-sm border p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-navy-900 flex items-center gap-2">
          <Inbox className="h-5 w-5" /> 客戶查詢（{list ? list.length : '…'}）
        </h2>
        <div className="flex items-center gap-2">
          <select
            className="border border-gray-200 rounded px-3 py-2 text-sm"
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
          >
            <option value="all">全部</option>
            <option value="new">新查詢</option>
            <option value="read">已讀</option>
            <option value="replied">已回覆</option>
          </select>
          <button
            onClick={load}
            className="flex items-center gap-1 text-sm px-4 py-2 rounded border border-navy-200 text-navy-700 hover:bg-navy-50"
          >
            <RefreshCw className="h-4 w-4" /> 重新整理
          </button>
        </div>
      </div>

      {list === null ? (
        <p className="text-center text-gray-400 py-10">載入中…</p>
      ) : visible.length === 0 ? (
        <p className="text-center text-gray-400 py-10">沒有查詢記錄。</p>
      ) : (
        <div className="space-y-3">
          {visible.map((inq) => (
            <div key={inq.id} className="border rounded-lg p-4">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${STATUS_STYLE[inq.status]}`}>
                    {STATUS_LABEL[inq.status]}
                  </span>
                  <span className="text-sm font-semibold text-gray-800">{inq.name}</span>
                  <span className="text-xs text-gray-400">
                    {new Date(inq.submittedAt).toLocaleString('zh-HK')}
                  </span>
                </div>
                <div className="flex gap-1">
                  {(['new', 'read', 'replied'] as const)
                    .filter((s) => s !== inq.status)
                    .map((s) => (
                      <button
                        key={s}
                        onClick={() => setStatus(inq.id, s)}
                        className="text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50"
                      >
                        標記為{STATUS_LABEL[s]}
                      </button>
                    ))}
                </div>
              </div>
              <div className="text-sm text-gray-600 space-y-0.5">
                <p>
                  <span className="text-gray-400">電郵：</span>
                  <a href={`mailto:${inq.email}`} className="text-navy-600 hover:underline">
                    {inq.email}
                  </a>
                  {inq.phone && (
                    <>
                      {' '}
                      <span className="text-gray-400">電話：</span>
                      {inq.phone}
                    </>
                  )}
                  {inq.company && (
                    <>
                      {' '}
                      <span className="text-gray-400">公司：</span>
                      {inq.company}
                    </>
                  )}
                </p>
                <p className="whitespace-pre-wrap border-t border-gray-100 pt-2 mt-2">{inq.message}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
